// ── Cloudinary media upload ───────────────────────────────────────────────────
// Uploads photos / videos / audio to Cloudinary using an *unsigned* upload
// preset, so it's safe to run entirely in the browser with no secret key.
//
// The cloud name + preset are PUBLIC-safe by design (unsigned presets are meant
// for client-side use). With either unset, the attachment feature stays dormant.
//
// Setup (one-time): create a free Cloudinary account → Settings → Upload →
// add an *unsigned* upload preset → copy the cloud name + preset name into:
//   REACT_APP_CLOUDINARY_CLOUD_NAME=...
//   REACT_APP_CLOUDINARY_UPLOAD_PRESET=...

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || '';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || '';

export const isCloudinaryConfigured = Boolean(CLOUD_NAME && UPLOAD_PRESET);

// Classify what kind of media we got back, so the UI can pick the right player.
// `fileType` is the original File's MIME type — the most reliable signal.
function classify(data, fileType = '') {
  if (fileType.startsWith('image/')) return 'image';
  if (fileType.startsWith('audio/')) return 'audio';
  if (fileType.startsWith('video/')) return 'video';
  // Fall back to Cloudinary's response when the MIME type is unknown.
  const audioFormats = ['mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'weba'];
  if (data.resource_type === 'image') return 'image';
  if (data.resource_type === 'video') {
    if (audioFormats.includes((data.format || '').toLowerCase())) return 'audio';
    // Cloudinary stores audio under "video"; audio has no visual dimensions.
    if (!data.width && !data.height) return 'audio';
    return 'video';
  }
  return 'raw';
}

// Upload one File/Blob. Resolves with a normalized attachment record.
// `onProgress(pct)` is called 0..100 during the upload (XHR gives us progress;
// fetch doesn't).
export function uploadToCloudinary(file, onProgress) {
  return new Promise((resolve, reject) => {
    if (!isCloudinaryConfigured) {
      reject(new Error('Cloudinary not configured'));
      return;
    }
    const form = new FormData();
    form.append('file', file);
    form.append('upload_preset', UPLOAD_PRESET);

    const xhr = new XMLHttpRequest();
    // "auto" lets Cloudinary detect image vs video vs raw automatically.
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/auto/upload`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({
            id: data.public_id || `att_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            url: data.secure_url,
            kind: classify(data, file?.type || ''),
            format: data.format || '',
            bytes: data.bytes || 0,
            name: data.original_filename || 'attachment',
            width: data.width,
            height: data.height,
          });
        } catch (err) {
          reject(new Error('Bad response from Cloudinary'));
        }
      } else {
        let msg = `Upload failed (${xhr.status})`;
        try { msg = JSON.parse(xhr.responseText)?.error?.message || msg; } catch {}
        reject(new Error(msg));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(form);
  });
}

// Human-readable file size.
export function prettyBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Shrink a photo before upload: cap the longest edge and recompress to JPEG.
// Phone photos are often 5–12MB; this typically cuts them 5–10× with no visible
// quality loss, so uploads finish far faster. Non-images (video/audio) and GIFs
// pass straight through untouched. Always falls back to the original on any error
// or if compression wouldn't actually save bytes.
export function compressImage(file, maxDim = 1920, quality = 0.82) {
  return new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith('image/') || file.type === 'image/gif') {
      resolve(file);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const longest = Math.max(width, height);
      if (longest > maxDim) {
        const scale = maxDim / longest;
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      try {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            // Keep the original if compression didn't help (e.g. already small).
            if (!blob || blob.size >= file.size) { resolve(file); return; }
            const name = (file.name || 'photo').replace(/\.\w+$/, '') + '.jpg';
            resolve(new File([blob], name, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          quality,
        );
      } catch {
        resolve(file);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}
